import groupBy from 'lodash/groupBy';

import { QuizEditorDetailsFragment } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/fragments/generated/QuizEditorContent';
import {
  DifficultyEnum,
  Maybe,
  QuestionGenerationType,
  QuestionsGenerationConfig,
  QuizSectionType,
  UpdateQuestionEntity,
} from '@/types/schema';

const PropertyToDifficultyEnum: Record<string, DifficultyEnum> = {
  easy: DifficultyEnum.Easy,
  medium: DifficultyEnum.Medium,
  hard: DifficultyEnum.Hard,
  notApplicable: DifficultyEnum.Na,
};

// util to get sum of all randomizeConfig limits
export const getRandomizeConfigTotalCount = (config?: Maybe<QuestionsGenerationConfig>): number => {
  if (!config?.randomizeConfig?.length) {
    return 0;
  }
  return config.randomizeConfig.reduce((sum, config) => sum + (config.limit ?? 0), 0);
};

export type UseQuizRandomizationState = {
  questionsCount: number;
  showError?: boolean;
  settings: Pick<QuizEditorDetailsFragment, 'questionGenerationType' | 'questionGenerationConfig'>;
};

type Params = Omit<UseQuizRandomizationState, 'showError'>;

// util to check if randomizeConfig limits sum is equal to questionsCount (the desired number of questions)
export const isQuestionGenerationConfigValid = (params: Params) => {
  const { settings, questionsCount } = params;
  const { questionGenerationType, questionGenerationConfig } = settings;

  if (
    questionGenerationType === QuestionGenerationType.Random ||
    questionGenerationType === QuestionGenerationType.Sequential
  ) {
    return getRandomizeConfigTotalCount(questionGenerationConfig) === questionsCount;
  }

  return true;
};

export const getQuestionsCountByDifficulty = (questions: UpdateQuestionEntity[]) => {
  const groupedQuestions = groupBy(questions, (q) => q.difficulty || DifficultyEnum.Na);

  return Object.values(DifficultyEnum).reduce(
    (accumulator, difficulty) => {
      accumulator[difficulty] = groupedQuestions[difficulty]?.length || 0;
      return accumulator;
    },
    {} as Record<DifficultyEnum, number>,
  );
};

type IsSettingsValidParams = {
  questions: UpdateQuestionEntity[];
  quiz: Pick<QuizEditorDetailsFragment, 'questionGenerationType' | 'questionGenerationConfig'>;
};

// util to check if question generation settings are valid - at time of publishing quiz
export const isQuestionGenerationSettingsValid = (params: IsSettingsValidParams) => {
  // if question generation type is not random, then settings are valid
  if (
    params.quiz.questionGenerationType !== QuestionGenerationType.Random &&
    params.quiz.questionGenerationType !== QuestionGenerationType.Sequential
  ) {
    return true;
  }

  const { quiz, questions } = params;
  const { questionGenerationConfig } = quiz;
  const randomizeTotalCount = getRandomizeConfigTotalCount(questionGenerationConfig);

  // if number of questions is less than the desired number of questions, then settings are invalid
  if (questions.length < randomizeTotalCount) {
    return false;
  }

  const questionsCountByDifficulty = getQuestionsCountByDifficulty(questions);

  // if any of the randomizeConfig limits is greater than the number of questions of that difficulty, then settings are invalid
  return questionGenerationConfig?.randomizeConfig?.every((config) => {
    const { propertyValue, limit } = config;
    const difficulty = PropertyToDifficultyEnum[propertyValue];
    return (limit ?? 0) <= questionsCountByDifficulty[difficulty];
  });
};

type SectionValidationParams = {
  sections: QuizSectionType[];
  questions: Array<UpdateQuestionEntity & { sectionId?: string | null }>;
};

export const isSectionRandomizationSettingsValid = (
  params: SectionValidationParams,
): { valid: boolean; invalidSectionId?: string } => {
  const { sections, questions } = params;
  const questionsBySectionId = groupBy(questions, (q) => q.sectionId ?? '__unsectioned__');

  for (const section of sections) {
    const config = section.randomizationConfig;
    if (!config?.questionGenerationType) continue;

    if (
      config.questionGenerationType !== QuestionGenerationType.Random &&
      config.questionGenerationType !== QuestionGenerationType.Sequential
    ) {
      continue;
    }

    const sectionQuestions = questionsBySectionId[section._id] ?? [];
    const randomizeTotalCount = getRandomizeConfigTotalCount(config.questionGenerationConfig);

    if (sectionQuestions.length < randomizeTotalCount) {
      return { valid: false, invalidSectionId: section._id };
    }

    const questionsCountByDifficulty = getQuestionsCountByDifficulty(sectionQuestions);
    const allValid = config.questionGenerationConfig?.randomizeConfig?.every((rc) => {
      const difficulty = PropertyToDifficultyEnum[rc.propertyValue];
      return (rc.limit ?? 0) <= questionsCountByDifficulty[difficulty];
    });

    if (!allValid) {
      return { valid: false, invalidSectionId: section._id };
    }
  }

  return { valid: true };
};
